/**
 * iter253 (Round 1, Part 5) — Listing workflow date+time formatter.
 *
 * Every listing workflow timestamp displayed in the CRM (Started,
 * Under Review, Second Start, Completed, Rejected, Assigned) MUST
 * show both DATE and TIME, in the shape:
 *
 *   18/08/2026 10:30 AM
 *
 * Never just "10:30 AM". If the input is falsy / unparseable,
 * returns an empty string so the calling component can render a
 * fallback like "—" instead of "Invalid Date".
 *
 * Standardised across every workflow surface (CX Leader listing
 * cards, CX employee action panels, LeadDetails timeline,
 * workflow history modals) — a single canonical helper avoids
 * per-page formatting drift.
 *
 * Also exports `formatElapsed(iso)` for the "how long since <stage>"
 * chip that CX Leader / CX Employee views show next to the active
 * timer. Returns things like '15m', '1h 05m', '2d 3h'. Empty string
 * for falsy / future timestamps.
 */

const _pad = (n) => String(n).padStart(2, '0');

/**
 * Format an ISO / Date / timestamp value into DD/MM/YYYY HH:MM AM/PM.
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export function formatDateTime(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = _pad(d.getDate());
  const mm = _pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const mins = _pad(d.getMinutes());
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${dd}/${mm}/${yyyy} ${_pad(hours)}:${mins} ${ampm}`;
}

/**
 * Human-friendly elapsed duration since the given timestamp.
 * @param {string|Date|null|undefined} since
 * @param {Date} [now]
 * @returns {string}
 */
export function formatElapsed(since, now = new Date()) {
  if (!since) return '';
  const d = since instanceof Date ? since : new Date(since);
  if (Number.isNaN(d.getTime())) return '';
  let secs = Math.floor((now - d) / 1000);
  if (secs < 0) return '';
  const days = Math.floor(secs / 86400); secs -= days * 86400;
  const hours = Math.floor(secs / 3600); secs -= hours * 3600;
  const mins = Math.floor(secs / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${_pad(mins)}m`;
  return `${mins}m`;
}
